import { useCallback } from "react";
import { useDataProvider, useGetIdentity, type DataProvider } from "ra-core";

import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  ALL_PROJECTS,
  useCurrentDealPipeline,
  useCurrentProject,
} from "../projects/useCurrentProject";
import { firstOpenStage } from "../deals/stages";
import type { PipelineStage } from "../types";
import { useCompanyResolver } from "./useCompanyResolver";
import { createEachRow } from "./createEachRow";
import { toConfiguredValues, toInteger, toIsoDate, toText } from "./parseCell";
import type { ImportRow, ProcessImportBatch } from "./types";

/** One CSV row, with the values needed before its deal can be created. */
type DealRow = {
  row: ImportRow;
  companyName: string | undefined;
  stage: PipelineStage | undefined;
};

/**
 * The stage a CSV cell names in the pipeline: by stage name, or by a value or
 * label of the old global stage list («proposal-sent», «КП отправлено»);
 * empty or unknown → the first open stage.
 */
const toStage = (
  cell: ImportRow[string],
  stages: PipelineStage[],
  legacy: { value: string; label: string }[],
): PipelineStage | undefined => {
  if (!stages.length) return undefined;
  const text = toText(cell)?.toLowerCase();
  const legacyLabel = legacy
    .find((o) => o.value.toLowerCase() === text)
    ?.label.toLowerCase();
  return (
    stages.find((s) => s.name.toLowerCase() === text) ??
    stages.find((s) => s.name.toLowerCase() === legacyLabel) ??
    firstOpenStage(stages)
  );
};

/**
 * Creates a deal per CSV row. Unknown columns are ignored, missing ones are
 * left empty — except `name`, which the database requires. The `company` column
 * holds a company name: matching companies are reused, unknown ones are created.
 */
export function useDealImport(): ProcessImportBatch {
  const { dealCategories, dealStages } = useConfigurationContext();
  const { identity } = useGetIdentity();
  // deals go to the project chosen in the header and its current pipeline
  // («Все проекты»: the board offers no import; refused here as well)
  const { currentId } = useCurrentProject();
  const projectId = currentId === ALL_PROJECTS ? null : currentId;
  const { pipeline, stages } = useCurrentDealPipeline(projectId);
  const dataProvider = useDataProvider();
  const getCompanies = useCompanyResolver();

  return useCallback(
    async (batch) => {
      if (projectId == null) {
        throw new Error("Выберите проект в шапке: сделки загружаются в него");
      }
      // Parse the company name and the stage once per row: both are needed to
      // resolve companies and column indexes up front, then again per deal.
      const rows: DealRow[] = batch.map((row) => ({
        row,
        companyName: toText(row.company),
        // a stage of the pipeline; an empty or unknown one → its first stage
        stage: toStage(row.stage, stages, dealStages),
      }));

      const [companies, indexes] = await Promise.all([
        getCompanies(
          rows
            .map(({ companyName }) => companyName)
            .filter((name): name is string => name !== undefined),
        ),
        appendedIndexes(rows, dataProvider),
      ]);

      const now = new Date().toISOString();
      return createEachRow(
        rows.map(({ row, companyName, stage }) =>
          dataProvider.create("deals", {
            data: {
              name: toText(row.name),
              company_id: companyName
                ? companies.get(companyName)?.id
                : undefined,
              contact_ids: [],
              categories: toConfiguredValues(row.categories, dealCategories),
              project_id: projectId ?? undefined,
              pipeline_id: pipeline?.id,
              stage_id: stage?.id,
              stage: stage?.name,
              description: toText(row.description),
              // amount lands in a bigint column, which rejects "4500.50"
              amount: toInteger(row.amount),
              expected_closing_date: toIsoDate(row.expected_closing_date),
              sales_id: identity?.id,
              index: indexes.get(row) ?? 0,
              created_at: now,
              updated_at: now,
            },
          }),
        ),
      );
    },
    [
      dataProvider,
      dealCategories,
      dealStages,
      getCompanies,
      identity?.id,
      projectId,
      pipeline?.id,
      stages,
    ],
  );
}

/**
 * One distinct index per imported deal, appended below the deals already in its
 * stage and keeping the CSV row order.
 *
 * The Kanban board sorts each column on `index`, and its drag handler shifts the
 * indexes of the cards around the drop target. A column whose deals all share
 * `index: 0` therefore cannot be reordered — the drop writes `index: 0` again
 * and the card snaps back — and its order is whatever the backend returns.
 */
const appendedIndexes = async (
  rows: DealRow[],
  dataProvider: DataProvider,
): Promise<Map<ImportRow, number>> => {
  const stageIds = [
    ...new Set(
      rows
        .map(({ stage }) => stage?.id)
        .filter((id): id is NonNullable<typeof id> => id != null),
    ),
  ];
  const lastIndexes = new Map(
    await Promise.all(
      stageIds.map(
        async (stageId) =>
          [stageId, await lastIndexOf(stageId, dataProvider)] as const,
      ),
    ),
  );

  return new Map(
    stageIds.flatMap((stageId) => {
      const firstIndex = (lastIndexes.get(stageId) ?? -1) + 1;
      return rows
        .filter((candidate) => candidate.stage?.id === stageId)
        .map(({ row }, position) => [row, firstIndex + position] as const);
    }),
  );
};

/** Highest index currently used in a stage, or -1 when it holds no deal. */
const lastIndexOf = async (
  stageId: PipelineStage["id"],
  dataProvider: DataProvider,
) => {
  const { data } = await dataProvider.getList("deals", {
    filter: { stage_id: stageId },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "index", order: "DESC" },
  });
  return data[0]?.index ?? -1;
};
